import X from '@unifyapps/icons/outline/X';
import clsx from 'clsx';
import { forwardRef, useMemo } from 'react';
import { decode18NInterpolatedString } from '../../utils/decode18NInterpolatedString';
import { Box } from '../Box';
import { Button } from '../Button';
import { SmallIconButton } from '../SmallIconButton';
import { Stack } from '../Stack';
import { TYPOGRAPHY_COMPONENTS, Typography } from '../Typography';
import type { ToastAction, ToastProps } from './types';

export const UA_TOAST = 'ua-toast';

function ToastStartDecorator({
  StartDecoratorComponent,
  startDecoratorNode,
}: {
  StartDecoratorComponent: ToastProps['startDecoratorComponent'];
  startDecoratorNode: ToastProps['startDecoratorNode'];
}) {
  if (!StartDecoratorComponent && !startDecoratorNode) return null;

  if (startDecoratorNode) {
    return startDecoratorNode;
  }

  if (StartDecoratorComponent) {
    return <StartDecoratorComponent className={clsx('ua-toast-icon')} />;
  }

  return null;
}

function ToastActionButton({ action, index }: { action: ToastAction; index: number }) {
  const defaultVariant = useMemo(() => {
    if (index === 0) return 'outline';
    return 'soft';
  }, [index]);

  return (
    <Button
      {...action}
      color={action.color ?? 'neutral'}
      component={action.href ? 'a' : 'button'}
      href={action.href}
      onClick={action.onClick}
      size="xs"
      target={action.href ? '_blank' : undefined}
      variant={action.variant ?? defaultVariant}
    >
      {action.text}
    </Button>
  );
}

function ToastActions({ actions, className }: { actions: ToastAction[]; className?: string }) {
  if (actions.length === 0) return null;

  return (
    <Box className={clsx('ua-toast-actions', className)}>
      {actions.map((action, index) => (
        <ToastActionButton action={action} index={index} key={action.text} />
      ))}
    </Box>
  );
}

const Toast = forwardRef<HTMLDivElement, ToastProps>((props, ref) => {
  const {
    className,
    startDecoratorComponent: StartDecoratorComponent,
    startDecoratorNode,
    color = 'default',
    title,
    description,
    actions = [],
    onClose,
    slotProps = {},
    bottomDecoratorNode,
    ...rest
  } = props;

  const closeButton = onClose && (
    <SmallIconButton
      Icon={X}
      onClick={onClose}
      size="sm"
      variant="solid"
      {...slotProps.closeButton}
      className={clsx(
        'transition-opacity duration-200 ua-toast-close-btn',
        slotProps.closeButton?.className,
      )}
    />
  );

  const hasActions = actions.length > 0;

  const content = (
    <Box className="ua-toast-content">
      <Box className="ua-toast-header">
        <ToastStartDecorator
          StartDecoratorComponent={StartDecoratorComponent}
          startDecoratorNode={startDecoratorNode}
        />
        <Box {...slotProps.info} className={clsx('ua-toast-info', slotProps.info?.className)}>
          {title && (
            // font styles moved to `.ua-toast-title` in toast.css so theme-manager can override them.
            // `variant` is dropped to avoid emitting `text-sm` utility that would beat the css rule.
            // `as` keeps the right semantic tag (Typography would otherwise fall back to `span`).
            <Typography
              as={TYPOGRAPHY_COMPONENTS['text-sm']}
              {...slotProps.title}
              className={clsx(
                'break-anywhere text-inherit ua-toast-title',
                slotProps.title?.className,
              )}
            >
              {decode18NInterpolatedString(title)}
            </Typography>
          )}
          {description && (
            // same reasoning as title — styles in `.ua-toast-description`, `as` preserves semantic tag.
            <Typography
              as={TYPOGRAPHY_COMPONENTS['text-xs']}
              {...slotProps.description}
              className={clsx(
                'break-anywhere ua-toast-description',
                slotProps.description?.className,
              )}
            >
              {description}
            </Typography>
          )}
          {bottomDecoratorNode ? bottomDecoratorNode : null}
        </Box>
      </Box>
      {hasActions && (
        <ToastActions actions={actions} className={clsx(slotProps.actions?.className)} />
      )}
    </Box>
  );

  return (
    <Stack
      {...slotProps.root}
      className={clsx('relative shadow-md', UA_TOAST, className, slotProps.root?.className)}
      data-color={color}
      direction="row"
      ref={ref}
      role="alert"
      {...rest}
    >
      {content}
      {closeButton}
    </Stack>
  );
});

Toast.displayName = 'Toast';

export default Toast;
